import { inject, Injectable } from '@angular/core';
import { WSS_API_BASE, ARROW } from '../../constants/general.constants';
import { ToastrService } from 'ngx-toastr';
import { ArrowAliasService } from '../arrow-alias/arrow-alias.service';
import { ArrowAliasV2 } from '../../interfaces/arrow-alias.interface';
import {
  ArrowClient,
  describeArrowError,
  type DataSet,
  type DatasetView,
  type FlightAction,
  type FlightInfo,
  type TableFromFileResult,
  type ViewTicket,
} from '@enlighten/ts-shared/arrow';
import type { MethodKey, StepParams } from '@enlighten/ts-shared/step-api';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';
import { ArrowHelper } from '../../helpers/arrow.helper';
import { AppStateService } from '../app-state/app-state.service';
import { AuthenticationService } from '../authentication/authentication.service';

/**
 * The params the shared parser produces for an `ARROW_FLIGHT` method (EHQ-10).
 * The bridges validate a step's arguments before a method here runs.
 */
type Params<M extends string> = StepParams[`ARROW_FLIGHT.${M}` & MethodKey];

/**
 * Represents a service for managing Arrow Websocket connections.
 * This service handles connecting to Arrow aliases, sending and receiving messages,
 * and managing the active connections.
 */
@Injectable({
  providedIn: 'root',
})
export class ArrowWebsocketService {
  private readonly authService = inject(AuthenticationService);
  private readonly toastr = inject(ToastrService);
  private readonly arrowAliasService = inject(ArrowAliasService);

  readonly #appState = inject(AppStateService);

  readonly #clients = new Map<string, ArrowClient>();
  #currentClient: ArrowClient | undefined;
  /** The `#clients` key of `#currentClient`. */
  #currentKey: string | undefined;

  // ─────────────────────────────────────────────────────────────────────
  // ─── Do connection ───────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async connectToAliasNamed(
    aliasName: string,
    context?: ExecutionContext,
    standalone = false
  ): Promise<ArrowClient> {
    const projectId = this.#appState.projectId();
    return new Promise((resolve, reject) => {
      const cacheKey = `${aliasName}-${projectId}`;
      if (!standalone) {
        const existing = this.#clients.get(cacheKey);
        if (existing) {
          resolve(existing);
          return;
        }
      }
      // need to connect and return the connection
      this.connect({ connectionName: aliasName }, context)
        .then(() => {
          if (this.#currentClient !== undefined) {
            const client = this.#currentClient;
            if (standalone) {
              this.#clients.delete(cacheKey);
              this.#currentClient = undefined;
              this.#currentKey = undefined;
            }
            resolve(client);
          } else {
            reject(new Error('failed to connect to alias'));
          }
        })
        .catch(() => {
          reject(new Error('failed to connect to alias'));
        });
    });
  }

  private validateAlias(aliasName: string): ArrowAliasV2 {
    const alias = this.getAliasByName(aliasName);
    if (!alias) {
      const error = new Error(`Alias "${aliasName}" not found. Please check that the alias exists and is configured correctly.`);
      this.toastr.error(`Alias "${aliasName}" not found`, 'Connection Error');
      throw error;
    }
    return alias;
  }

  private async setupNewConnection(alias: ArrowAliasV2, cacheKey: string, chunkContext?: ExecutionContext): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      const error = new Error('Authentication token not available');
      if (chunkContext) {
        chunkContext.addMessage('Authentication token not available', 'danger');
      }
      throw error;
    }

    const url = this.buildDatafusionWebSocketUrl(alias.aliasId, token);
    const client = new ArrowClient({ url, connectionId: alias.aliasId });

    this.#clients.set(cacheKey, client);
    this.#currentClient = client;
    this.#currentKey = cacheKey;
    client.wsClosedCallback = () => {
      this.#clients.delete(cacheKey);
      if (this.#currentClient === client) {
        this.#currentClient = undefined;
        this.#currentKey = undefined;
      }
    };
    await client.connect();
  }

  public async connect(params: Params<'connect'>, chunkContext?: ExecutionContext): Promise<void> {
    const alias = this.validateAlias(params.connectionName);
    const cacheKey = `${alias.name}-${alias.projectId}`;

    const existing = this.#clients.get(cacheKey);
    if (existing) {
      if (!existing.connected) {
        this.#clients.delete(cacheKey);
      } else {
        this.#currentClient = existing;
        this.#currentKey = cacheKey;
        return;
      }
    }

    try {
      await this.setupNewConnection(alias, cacheKey, chunkContext);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (chunkContext) {
        chunkContext.addMessage(`Connection error: ${errorMessage}`, 'danger');
      }
      throw error;
    }
  }

  private getAuthToken(): string | null {
    try {
      return this.authService.getAccessToken() || null;
    } catch (error) {
      console.error('Failed to get authentication token:', error);
      throw new Error('Authentication token not available');
    }
  }

  private buildDatafusionWebSocketUrl(aliasId: number, token: string): string {
    // Avoid regex /\/+$/ - vulnerable to ReDoS due to backtracking (typescript:S5852)
    const baseUrl = this.trimTrailingSlashes(WSS_API_BASE);
    const workflowId = this.#appState.workflowId();
    return `${baseUrl}/${ARROW}/${aliasId}?token=${encodeURIComponent(token)}&workflow_id=${workflowId}`;
  }

  /** Removes trailing slashes without regex to avoid ReDoS (typescript:S5852) */
  private trimTrailingSlashes(s: string): string {
    let result = s;
    while (result.endsWith('/')) {
      result = result.slice(0, -1);
    }
    return result;
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Do conenction ───────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async disconnect(_params?: Params<'disconnect'>): Promise<void> {
    if (!this.#currentClient) {
      throw new Error('No active connection to disconnect');
    }
    await this.#currentClient.disconnect();
    if (this.#currentKey !== undefined) {
      this.#clients.delete(this.#currentKey);
    }
    this.#currentClient = undefined;
    this.#currentKey = undefined;
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Helpers ─────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────
  private getAliasByName(name: string): ArrowAliasV2 | undefined {
    return this.arrowAliasService
      .aliasV2List()
      .find((item: ArrowAliasV2) => item.name === name);
  }

  /**
   * Returns the active client.
   * @param operationName Name of the operation for error messages
   * @param context Optional execution context for error messages
   * @throws Error if no active connection
   */
  private ensureClientConnected(operationName: string, context?: ExecutionContext): ArrowClient {
    if (this.#currentClient === undefined) {
      const error = new Error(`No active connection to ${operationName}`);
      if (context) {
        context.addMessage(`No active connection to ${operationName}`, 'danger');
      }
      throw error;
    }
    return this.#currentClient;
  }

  /**
   * Handles errors consistently across methods
   * @param error The error that occurred
   * @param operationName Name of the operation for error messages
   * @param context Optional execution context for error messages
   * @throws The original error after logging
   */
  private handleError(
    error: unknown,
    operationName: string,
    context?: ExecutionContext
  ): never {
    if (context) {
      context.addMessage(`Error ${operationName}: ${describeArrowError(error)}`, 'danger');
    }
    throw error; // Rethrow to allow try/catch blocks to work
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Shared Methods ──────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async createDataSet(
    params: Params<'createDataSet'>,
    chunkContext: ExecutionContext
  ): Promise<void> {
    const client = this.ensureClientConnected('create data set', chunkContext);
    try {
      const { dataSetName, basePath, partitionCols, batchSize, connectionName } = params;
      return await client.createDataSet(dataSetName, basePath, partitionCols, batchSize, connectionName);
    } catch (error: unknown) {
      this.handleError(error, 'creating data set', chunkContext);
    }
  }

  public async deleteDataSet(params: Params<'deleteDataSet'>, context?: ExecutionContext): Promise<void> {
    const client = this.ensureClientConnected('delete data set', context);
    try {
      return await client.deleteDataSet(params.dataSetName);
    } catch (error: unknown) {
      this.handleError(error, 'deleting data set', context);
    }
  }

  public async createView(params: Params<'createView'>, context?: ExecutionContext): Promise<ViewTicket> {
    const client = this.ensureClientConnected('create view', context);
    try {
      const { dataSetName, viewName, expiresAfter, partitionRegex, query } = params;
      return await client.createView(dataSetName, viewName, expiresAfter, partitionRegex, query);
    } catch (error: unknown) {
      this.handleError(error, 'creating view', context);
    }
  }

  public async deleteView(params: Params<'deleteView'>, context?: ExecutionContext): Promise<void> {
    const client = this.ensureClientConnected('delete view', context);
    try {
      return await client.deleteView(params.dataSetName, params.viewName);
    } catch (error: unknown) {
      this.handleError(error, 'deleting view', context);
    }
  }

  public async listDataSets(_params: Params<'listDataSets'>, context?: ExecutionContext): Promise<DataSet[]> {
    const client = this.ensureClientConnected('list data sets', context);
    return client.listDataSets();
  }

  public async refreshDataSetList(): Promise<DataSet[]> {
    return this.ensureClientConnected('refresh data set list').refreshDataSetList();
  }

  public async listCloud(params: Params<'listCloud'>): Promise<unknown> {
    const { connectionName, prefix, delimiter, maxKeys } = params;
    return this.ensureClientConnected('list cloud').listCloud(connectionName, prefix, delimiter, maxKeys);
  }

  public async listCloudDirs(params: Params<'listCloudDirs'>): Promise<unknown> {
    return this.ensureClientConnected('list cloud dirs').listCloudDirs(params.connectionName, params.prefix);
  }

  public async cloudBasePathExists(params: Params<'cloudBasePathExists'>): Promise<boolean> {
    return this.ensureClientConnected('check cloud base path').cloudBasePathExists(
      params.connectionName,
      params.basePath
    );
  }

  public async listViews(params: Params<'listViews'>): Promise<DatasetView[]> {
    return this.ensureClientConnected('list views').listViews(params.dataSetName);
  }

  public async getSchema(params: Params<'getSchema'>): Promise<unknown> {
    return this.ensureClientConnected('get schema').getSchema(params.dataSetName);
  }

  public async initCache(params: Params<'initCache'>): Promise<void> {
    return this.ensureClientConnected('init cache').initCache(params.dataSetName);
  }

  public async performQuery(params: Params<'performQuery'>, context?: ExecutionContext): Promise<Uint8Array> {
    const client = this.ensureClientConnected('perform query', context);
    return client.performQuery(params.dataSetName, params.query, false, params.params);
  }

  public async performQueryAsJson(params: Params<'performQueryAsJson'>, context?: ExecutionContext): Promise<string> {
    const client = this.ensureClientConnected('query data as json', context);
    try {
      // the asTable arg guarantees that the data is returned as arrow table
      const table = await client.performQuery(params.dataSetName, params.query, true, params.params);
      return JSON.stringify(ArrowHelper.tableToRowJson(table));
    } catch (error: unknown) {
      this.handleError(error, 'performing query as JSON', context);
    }
  }

  public async saveQueryAsTable(params: Params<'saveQueryAsTable'>): Promise<string> {
    return this.ensureClientConnected('save query as table').saveQueryAsTable(
      params.query,
      params.tableName,
      params.params
    );
  }

  public async saveQueryToCloud(params: Params<'saveQueryToCloud'>): Promise<void> {
    const { query, connectionName, path } = params;
    return this.ensureClientConnected('save query to cloud').saveQueryToCloud(
      query,
      connectionName,
      path,
      params.params
    );
  }

  public async doAction(params: Params<'doAction'>): Promise<string> {
    return this.ensureClientConnected('do action').doAction(params.action, params.argument);
  }

  /**
   * Ingests a file from S3 storage directly into a dataset on the server.
   * Eliminates client-side memory usage for large file imports.
   */
  public async ingestFromStorage(params: Params<'ingestFromStorage'>): Promise<string> {
    const client = this.ensureClientConnected('ingest from storage');
    const { sourceConnection, sourcePath, datasetName, basePath, options } = params;
    const argument = JSON.stringify({
      sourceConnection,
      sourcePath,
      datasetName,
      basePath,
      ...options,
    });
    return client.doAction('IngestFromStorage', argument);
  }

  public async createPut(params: Params<'createPut'>): Promise<number> {
    const { dataSetName, basePath, partitionCols, batchSize, table, connectionName } = params;
    return this.ensureClientConnected('create put').createPut(
      dataSetName,
      basePath,
      partitionCols,
      batchSize,
      table,
      connectionName
    );
  }

  public async initPut(params: Params<'initPut'>): Promise<number> {
    return this.ensureClientConnected('init put').initPut(params.dataSetName);
  }

  public async continuePut(params: Params<'continuePut'>): Promise<void> {
    return this.ensureClientConnected('continue put').continuePut(params.putId, params.table);
  }

  public async endPut(params: Params<'endPut'>): Promise<void> {
    return this.ensureClientConnected('end put').endPut(params.putId);
  }

  public async listTables(): Promise<unknown> {
    return this.ensureClientConnected('list tables').listTables();
  }

  public async createTable(params: Params<'createTable'>): Promise<unknown> {
    return this.ensureClientConnected('create table').createTable(params.params);
  }

  public async uploadTable(params: Params<'uploadTable'>): Promise<void> {
    return this.ensureClientConnected('upload table').uploadTable(params.tableName, params.table);
  }

  public async updateTables(params: Params<'updateTables'>): Promise<void> {
    return this.ensureClientConnected('update tables').updateTables(params.sourceType, params.connectionName);
  }

  public async deleteTable(params: Params<'deleteTable'>): Promise<void> {
    return this.ensureClientConnected('delete table').deleteTable(params.tableName);
  }

  public async performDbQuery(params: Params<'performDbQuery'>, context?: ExecutionContext): Promise<Uint8Array> {
    const client = this.ensureClientConnected('perform db query', context);
    try {
      return await client.performDbQuery(params.connectionName, params.query, false, params.params);
    } catch (error: unknown) {
      this.handleError(error, 'performing db query', context);
    }
  }

  public async performDbQueryAsJson(params: Params<'performDbQueryAsJson'>, context?: ExecutionContext): Promise<string> {
    const client = this.ensureClientConnected('perform db query as JSON', context);
    try {
      return await client.performDbQueryAsJson(params.connectionName, params.query, params.params);
    } catch (error: unknown) {
      this.handleError(error, 'performing db query as JSON', context);
    }
  }

  public async createTableFromQuery(params: Params<'createTableFromQuery'>, context?: ExecutionContext): Promise<unknown> {
    const client = this.ensureClientConnected('create table from query', context);
    try {
      const { tableName, connectionName, query } = params;
      return await client.createTableFromQuery(tableName, connectionName, query, params.params);
    } catch (error: unknown) {
      this.handleError(error, 'creating table from query', context);
    }
  }

  /**
   * Execute a write statement (INSERT, UPDATE, DELETE, DDL) against a database
   * connection. Returns JSON: {"rows_affected": N}
   */
  public async writeSQL(params: Params<'writeSQL'>, context?: ExecutionContext): Promise<string> {
    const client = this.ensureClientConnected('write SQL', context);
    try {
      return await client.writeSQL(params.connectionName, params.query, params.params);
    } catch (error: unknown) {
      this.handleError(error, 'writing SQL', context);
    }
  }

  /**
   * Bulk-load a DataFusion table into a database table via COPY.
   * Returns JSON: {"rows_inserted": N}
   */
  public async writeTableToDb(params: Params<'writeTableToDb'>, context?: ExecutionContext): Promise<string> {
    const client = this.ensureClientConnected('write table to db', context);
    try {
      const { tableName, connectionName, targetTable, mode } = params;
      return await client.writeTableToDb(tableName, connectionName, targetTable, mode);
    } catch (error: unknown) {
      this.handleError(error, 'writing table to db', context);
    }
  }

  public async listActions(_params: Params<'listActions'>, context?: ExecutionContext): Promise<readonly FlightAction[]> {
    const client = this.ensureClientConnected('list actions', context);
    try {
      return await client.listActions();
    } catch (error: unknown) {
      this.handleError(error, 'listing actions', context);
    }
  }

  public async listFlights(params: Params<'listFlights'>, context?: ExecutionContext): Promise<readonly FlightInfo[]> {
    const client = this.ensureClientConnected('list flights', context);
    try {
      return await client.listFlights(params.criteria, params.binary);
    } catch (error: unknown) {
      this.handleError(error, 'listing flights', context);
    }
  }

  public async getFlightInfo(params: Params<'getFlightInfo'>, context?: ExecutionContext): Promise<FlightInfo> {
    const client = this.ensureClientConnected('get flight info', context);
    try {
      return await client.getFlightInfo(params.flight);
    } catch (error: unknown) {
      this.handleError(error, 'getting flight info', context);
    }
  }

  public async createTableFromFile(
    params: Params<'createTableFromFile'>,
    context?: ExecutionContext
  ): Promise<TableFromFileResult> {
    const client = this.ensureClientConnected('create table from file', context);
    try {
      return await client.createTableFromFile(params.filename, params.tableName, params.options);
    } catch (error: unknown) {
      this.handleError(error, 'creating table from file', context);
    }
  }
}
